// src/pages/Scripts.tsx - Updated with mobile responsive design
import React, { useEffect, useState, useRef } from 'react';
import { Card, Table, Button, Modal, message, Tag, Input, Space } from 'antd';
import {
  EyeOutlined,
  CopyOutlined,
  DownloadOutlined,
  SearchOutlined,
  FileTextOutlined,
  FilePdfOutlined
} from '@ant-design/icons';
import { PDFViewerModal } from '../components/pdf-viewer';
import { useLocation } from 'react-router-dom';
import { authenticatedFetch } from '../providers/authProvider';

const { Search } = Input;

interface ScriptJob {
  job_id: string;
  original_filename?: string;
  extracted_address?: string;  // v1.2: Auto-extracted property address
  created_at: any;
  status: string;
  generation_type?: string;
  planType?: string;
  tasks: {
    create_script: {
      status: string;
      script_text?: string;
      error?: string;
    };
  };
}

// Helper to get display title - prefer extracted_address over filename
const getDisplayTitle = (job: ScriptJob): string => {
  if (job.extracted_address) {
    return job.extracted_address;
  }
  if (job.original_filename) {
    return job.original_filename.replace('.pdf', '');
  }
  return `Script-${job.job_id.substring(0, 8)}`;
};

const Scripts: React.FC = () => {
  const [scripts, setScripts] = useState<ScriptJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewingScript, setViewingScript] = useState<ScriptJob | null>(null);
  const [searchText, setSearchText] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const mountedRef = useRef(true);
  const fetchAttemptRef = useRef(0);

  // PDF Viewer state
  const [pdfViewerState, setPdfViewerState] = useState<{
    visible: boolean;
    pdfUrl: string;
    documentId: string;
    documentTitle: string;
  }>({ visible: false, pdfUrl: '', documentId: '', documentTitle: '' });

  const location = useLocation();

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    fetchUserScripts();
    
    const params = new URLSearchParams(location.search);
    const viewJobId = params.get('view');
    if (viewJobId) {
      fetchSpecificScript(viewJobId);
    }
  }, [location]);

  const fetchUserScripts = async () => {
    if (!mountedRef.current) return;
    
    try {
      setLoading(true);
      const response = await authenticatedFetch('/user-jobs');
      
      if (!response.ok) {
        throw new Error('Failed to fetch scripts');
      }
      
      const data = await response.json();
      const scriptJobs = data.jobs?.filter((job: ScriptJob) => 
        job.tasks?.create_script?.status === 'COMPLETED'
      ) || [];
      
      if (mountedRef.current) {
        setScripts(scriptJobs);
        fetchAttemptRef.current = 0; // Reset attempts on success
      }
    } catch (error) {
      // Only log errors after all retries have failed
      if (fetchAttemptRef.current >= 2) {
        console.error('Error fetching scripts:', error);
      }
      
      // Retry if it's likely an auth timing issue (on initial load)
      if (fetchAttemptRef.current < 3 && mountedRef.current) {
        fetchAttemptRef.current++;
        setTimeout(() => {
          if (mountedRef.current) {
            fetchUserScripts();
          }
        }, 1000); // Wait 1 second before retry
      } else if (mountedRef.current && fetchAttemptRef.current >= 3) {
        message.error('Failed to fetch scripts');
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  };

  const fetchSpecificScript = async (jobId: string) => {
    if (!mountedRef.current) return;
    
    try {
      const response = await authenticatedFetch('/user-jobs');
      
      if (!response.ok) {
        throw new Error('Failed to fetch script');
      }
      
      const data = await response.json();
      const job = data.jobs?.find((j: ScriptJob) => j.job_id === jobId);
      
      if (job && job.tasks?.create_script?.status === 'COMPLETED' && mountedRef.current) {
        setViewingScript(job);
      } else if (mountedRef.current) {
        message.error('Script not found or not completed');
      }
    } catch (error) {
      // Suppress auth errors during initial load
      if (fetchAttemptRef.current >= 2) {
        console.error('Error fetching specific script:', error);
      }
      if (mountedRef.current && fetchAttemptRef.current >= 3) {
        message.error('Failed to fetch script');
      }
    }
  };

  const cleanScriptText = (text: string): string => {
    return text.replace(/\[(.*?):\d+\]/g, '$1');
  };

  const copyToClipboard = (text: string) => {
    const cleanedText = cleanScriptText(text);
    navigator.clipboard.writeText(cleanedText).then(() => {
      message.success('Script copied to clipboard!');
    }).catch(() => {
      message.error('Failed to copy script');
    });
  };

  const downloadScript = (script: ScriptJob) => {
    const scriptText = script.tasks?.create_script?.script_text;
    if (!scriptText) return;

    const cleanedText = cleanScriptText(scriptText);
    const element = document.createElement('a');
    const file = new Blob([cleanedText], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = `${script.original_filename?.replace('.pdf', '') || 'script'}_script.txt`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
    
    message.success('Script downloaded!');
  };

  const getScriptPreview = (text: string) => {
    const cleanedText = cleanScriptText(text);
    const previewLength = isMobile ? 80 : 150;
    return cleanedText.length > previewLength ? cleanedText.substring(0, previewLength) + '...' : cleanedText;
  };

  const handleViewPDF = async (script: ScriptJob) => {
    try {
      // Fetch PDF as blob through authenticated proxy to avoid CORS issues
      const response = await authenticatedFetch(`/proxy-pdf/${script.job_id}`);

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Failed to load PDF' }));
        throw new Error(error.error || 'Failed to load PDF');
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      // Open PDF viewer modal
      setPdfViewerState({
        visible: true,
        pdfUrl: blobUrl,
        documentId: script.job_id,
        documentTitle: getDisplayTitle(script),
      });
    } catch (error: any) {
      console.error('Error getting PDF:', error);
      message.error(error.message || 'Failed to load PDF');
    }
  };

  const filteredScripts = scripts.filter(script => {
    if (!searchText) return true;
    
    const searchLower = searchText.toLowerCase();
    const filename = script.original_filename?.toLowerCase() || '';
    const scriptText = cleanScriptText(script.tasks?.create_script?.script_text || '').toLowerCase();
    
    return filename.includes(searchLower) || scriptText.includes(searchLower);
  });

  // Mobile Card Component
  const MobileScriptCard = ({ script }: { script: ScriptJob }) => {
    const cleanedText = cleanScriptText(script.tasks?.create_script?.script_text || '');
    const wordCount = cleanedText.split(/\s+/).filter(word => word.length > 0).length;
    const dateObj = script.created_at?.seconds ? new Date(script.created_at.seconds * 1000) : new Date(script.created_at);
    
    return (
      <Card className="mb-4" size="small" style={{ marginBottom: '12px' }}>
        {/* Header */}
        <div className="mb-3">
          <h4 className="font-medium text-base mb-1">
            {getDisplayTitle(script)}
          </h4>
          <div className="flex flex-wrap items-center gap-2">
            {script.generation_type === 'script_only' ? (
              <Tag color="blue" className="m-0">Script Only</Tag>
            ) : (
              <Tag color="green" className="m-0">Video Script</Tag>
            )}
            {script.planType && (
              <Tag color="default" className="m-0">{script.planType}</Tag>
            )}
            <span className="text-xs text-gray-500">{wordCount.toLocaleString()} words</span>
          </div>
        </div>

        {/* Preview */}
        <div className="text-sm text-gray-600 mb-3">
          {getScriptPreview(script.tasks?.create_script?.script_text || '')}
        </div>

        {/* Date */}
        <div className="text-sm text-gray-500" style={{ marginTop: '5px', marginBottom: '16px' }}>
          {dateObj.toLocaleDateString()} at {dateObj.toLocaleTimeString()}
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
          <Button
            icon={<FilePdfOutlined />}
            onClick={() => handleViewPDF(script)}
            size="small"
            style={{ flex: '1 1 45%' }}
          >
            Comment on Report
          </Button>
          <Button
            type="primary"
            icon={<EyeOutlined />}
            onClick={() => setViewingScript(script)}
            size="small"
            style={{ flex: '1 1 45%' }}
          >
            View Script
          </Button>
          <Button
            icon={<CopyOutlined />}
            onClick={() => copyToClipboard(script.tasks?.create_script?.script_text || '')}
            size="small"
            style={{ flex: '1 1 45%' }}
          >
            Copy
          </Button>
          <Button
            icon={<DownloadOutlined />}
            onClick={() => downloadScript(script)}
            size="small"
            style={{ flex: '1 1 45%' }}
          >
            Download
          </Button>
        </div>
      </Card>
    );
  };

  // Desktop table columns
  const columns = [
    {
      title: 'Reports',
      key: 'script',
      render: (record: ScriptJob) => (
        <div>
          <div className="font-medium mb-1">
            {getDisplayTitle(record)}
          </div>
          <div className="text-sm text-gray-500 mb-2">
            {getScriptPreview(record.tasks?.create_script?.script_text || '')}
          </div>
          <div className="flex gap-2">
            {record.generation_type === 'script_only' ? (
              <Tag color="blue">Script Only</Tag>
            ) : (
              <Tag color="green">Video Script</Tag>
            )}
            {record.planType && (
              <Tag color="default">{record.planType}</Tag>
            )}
          </div>
        </div>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created',
      width: 120,
      render: (date: any) => {
        const dateObj = date?.seconds ? new Date(date.seconds * 1000) : new Date(date);
        return (
          <div>
            <div className="text-sm">{dateObj.toLocaleDateString()}</div>
            <div className="text-xs text-gray-500">{dateObj.toLocaleTimeString()}</div>
          </div>
        );
      },
    },
    {
      title: 'Word Count',
      key: 'wordCount',
      width: 100,
      render: (record: ScriptJob) => {
        const cleanedText = cleanScriptText(record.tasks?.create_script?.script_text || '');
        const wordCount = cleanedText.split(/\s+/).filter(word => word.length > 0).length;
        return (
          <div className="text-center">
            <div className="font-medium">{wordCount.toLocaleString()}</div>
            <div className="text-xs text-gray-500">words</div>
          </div>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 320,
      render: (record: ScriptJob) => (
        <Space wrap>
          <Button
            icon={<FilePdfOutlined />}
            onClick={() => handleViewPDF(record)}
            size="small"
            title="Comment on Report"
          >
            Comment on Report
          </Button>
          <Button
            type="primary"
            icon={<EyeOutlined />}
            onClick={() => setViewingScript(record)}
            size="small"
          >
            View
          </Button>
          <Button
            icon={<CopyOutlined />}
            onClick={() => copyToClipboard(record.tasks?.create_script?.script_text || '')}
            size="small"
          >
            Copy
          </Button>
          <Button
            icon={<DownloadOutlined />}
            onClick={() => downloadScript(record)}
            size="small"
          >
            Download
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div className="max-w-6xl mx-auto p-6">
      <Card 
        title={
          !isMobile ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FileTextOutlined />
              <span>My Text Reviews</span>
            </div>
          ) : undefined
        }
        extra={
          !isMobile && (
            <Space>
              <Search
                placeholder="Search scripts..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                style={{ width: 250 }}
                prefix={<SearchOutlined />}
              />
              <Button 
                onClick={fetchUserScripts} 
                loading={loading}
              >
                Refresh
              </Button>
            </Space>
          )
        }
      >
        {/* Custom header for mobile */}
        {isMobile && (
          <>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between', 
              marginBottom: '16px',
              paddingBottom: '16px',
              borderBottom: '1px solid #f0f0f0'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FileTextOutlined />
                <span style={{ fontSize: '16px', fontWeight: 500 }}>My Text Reviews</span>
              </div>
              <Button 
                onClick={fetchUserScripts} 
                loading={loading}
                size="small"
              >
                Refresh
              </Button>
            </div>
            <Search
              placeholder="Search scripts..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: '100%', marginBottom: '16px' }}
              prefix={<SearchOutlined />}
            />
          </>
        )}
        
        {/* Desktop header using Card title */}
        {!isMobile && (
          <div style={{ display: 'none' }} />
        )}
        {/* Mobile view shows cards */}
        {isMobile ? (
          <>
            {filteredScripts.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                {searchText 
                  ? `No scripts found matching "${searchText}"` 
                  : 'No scripts generated yet'}
              </div>
            ) : (
              <>
                {filteredScripts.map((script) => (
                  <MobileScriptCard key={script.job_id} script={script} />
                ))}
              </>
            )}
          </>
        ) : (
          // Desktop view shows table
          <Table 
            dataSource={filteredScripts} 
            columns={columns} 
            loading={loading}
            rowKey="job_id"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => 
                `${range[0]}-${range[1]} of ${total} scripts`,
            }}
            locale={{
              emptyText: searchText 
                ? `No scripts found matching "${searchText}"` 
                : 'No scripts generated yet'
            }}
          />
        )}
      </Card>

      {/* Script Viewer Modal */}
      <Modal
        title={
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileTextOutlined />
              <span>Text Report Content</span>
            </div>
            {viewingScript && !isMobile && (
              <div className="text-sm text-gray-500 font-normal">
                {getDisplayTitle(viewingScript)}
              </div>
            )}
          </div>
        }
        open={!!viewingScript}
        onCancel={() => setViewingScript(null)}
        footer={[
          <Button 
            key="copy" 
            icon={<CopyOutlined />} 
            onClick={() => {
              if (viewingScript?.tasks?.create_script?.script_text) {
                copyToClipboard(viewingScript.tasks.create_script.script_text);
              }
            }}
          >
            Copy Script
          </Button>,
          <Button 
            key="download" 
            icon={<DownloadOutlined />} 
            onClick={() => {
              if (viewingScript) {
                downloadScript(viewingScript);
              }
            }}
          >
            Download
          </Button>,
          <Button key="close" onClick={() => setViewingScript(null)}>
            Close
          </Button>,
        ]}
        width={isMobile ? '95%' : 900}
        className="script-modal"
      >
        {viewingScript && (
          <div>
            {/* Script Metadata - only show on mobile */}
            {isMobile && (
              <div className="mb-4 p-3 bg-gray-50 rounded">
                <div className="text-sm font-medium">
                  {getDisplayTitle(viewingScript)}
                </div>
              </div>
            )}

            {/* Script Content */}
            <div 
              className="max-h-96 overflow-y-auto p-4 bg-white border rounded font-mono text-sm leading-relaxed"
              style={{ whiteSpace: 'pre-wrap' }}
            >
              {cleanScriptText(viewingScript.tasks?.create_script?.script_text || '')}
            </div>
          </div>
        )}
      </Modal>

      {/* PDF Viewer Modal */}
      <PDFViewerModal
        visible={pdfViewerState.visible}
        onClose={() => setPdfViewerState({ visible: false, pdfUrl: '', documentId: '', documentTitle: '' })}
        pdfUrl={pdfViewerState.pdfUrl}
        documentId={pdfViewerState.documentId}
        documentTitle={pdfViewerState.documentTitle}
      />
    </div>
  );
};

export default Scripts;